import React, { useEffect, useState, useCallback } from 'react';
import { useHistory, useLocation, Link } from 'react-router-dom';
import InfiniteScroll from 'react-infinite-scroll-component';
import Masonry, { ResponsiveMasonry } from 'react-responsive-masonry';
import { isEmpty, sortBy, uniqBy } from 'lodash';
import { useDispatch, useSelector } from 'react-redux';
import { AnyAction } from 'redux';
import { ThunkDispatch } from 'redux-thunk';
import { useTranslation } from 'react-i18next';
import { Col, Container, Row } from 'react-bootstrap';
import Chat from '../Chat/Chat';
import PlaceholderCardTile from './PlaceholderCardTile';
import Loader from '../../Components/Utility/Loader/Loader';
import ConfirmationPopupBase from '../../Components/ModalPopup/ConfirmationPopup';
import CustomTooltip from '../../Components/Tooltip/CustomTooltip';
import { NewSearch } from '../../Components/PIM/common';
import { AddIcon, DeleteIcon, ArchiveIcon } from '../../bombiix-icon';
import { useComponent } from '../UserSetting/UsersLibrary/UserRights';
import { getSubBrandAccess } from '../SystemMasters/Store/SystemMasters.action';
import { getPlaceholderCardData, addPlaceholderCard, deletePlaceholderCardData, archivePlaceholderCardData } from './store/placeholder.actions';
import { PAGE_SIZE } from './placeholder.constants';
import { PlaceholderCardItem } from './types';
import { PlaceholderDetailsState } from './store/placeholder.reducer';
import './placeholder.css';

type PlaceholderThunkDispatch = ThunkDispatch<unknown, unknown, AnyAction>;

const ConfirmationPopup = ConfirmationPopupBase as unknown as React.FC<{
  onOpen: boolean;
  onConfirm: () => void;
  title: React.ReactNode;
  message: React.ReactNode;
  toggle: () => void;
  loading?: boolean;
  isCommentOn?: boolean;
  commentText?: (_e: React.ChangeEvent<HTMLInputElement>) => void;
  comment?: string;
  showCommentHeader?: boolean;
}>;

type CheckActionPermissionFn = (_userRights: Record<string, unknown>, _section: string, _module: string | null, _action: string) => boolean;

interface CardListSelectorState {
  UserDetails: { isSupplierLoggedIn: boolean; userInfo: unknown };
  PlaceholderDetails: PlaceholderDetailsState;
  Users: { logInRights: unknown };
}

interface CardListState {
  selectedIdArray: string[];
  cards: PlaceholderCardItem[];
  searchText: string;
  hasMore: boolean;
  pageIndex: number;
  pageSize: number;
  archiveSection: boolean;
  showArchivePopup: boolean;
  archivedId: string | null;
  archiveComment: string;
  showDeletePopup: boolean;
  deleteId: string | null;
  loading: boolean;
  showMobileSearch: boolean;
  searchLoaded: boolean;
  subBrandAccess: boolean;
}

/**
 * Placeholder-management card listing page.
 * Ports the legacy CardList component to TypeScript, keeping the infinite-scroll
 * grid, archive toggle, debounced search, and edit/pin/archive/delete flows intact.
 */
function CardListPage(): React.ReactElement {
  const history = useHistory();
  const navigate = history.push as unknown as (_path: string, _state?: { fromArchive?: boolean | undefined }) => void;
  const dispatch = useDispatch<PlaceholderThunkDispatch>();
  const location = useLocation();
  const { t } = useTranslation('common');
  const searchParams = new URLSearchParams(location?.search);
  const locaStates = location?.state as { fromArchive?: boolean } | undefined;
  const fromArch = searchParams.get('fromArchive');
  const callArchiveApi = locaStates?.fromArchive || Boolean(fromArch);
  const {
    UserDetails: { isSupplierLoggedIn, userInfo },
    PlaceholderDetails: { placeholderCardList, totalCount },
    Users: { logInRights },
  } = useSelector((reducer: CardListSelectorState) => reducer);

  const [userRights, setUserRights] = useState<Record<string, unknown>>({});
  useEffect(() => {
    if (logInRights) {
      setUserRights(logInRights as Record<string, unknown>);
    }
  }, [logInRights]);
  const { CheckActionPermission } = useComponent() as unknown as { CheckActionPermission: CheckActionPermissionFn };
  const PlaceholderAddEditAccess = CheckActionPermission(userRights, 'placeholder', null, 'addEdit');
  const PlaceholderDeleteAccess = CheckActionPermission(userRights, 'placeholder', null, 'delete');
  const [state, setState] = useState<CardListState>({
    selectedIdArray: [],
    cards: [],
    searchText: '',
    hasMore: false,
    pageIndex: 1,
    pageSize: PAGE_SIZE,
    archiveSection: false,
    showArchivePopup: false,
    archivedId: '',
    archiveComment: '',
    showDeletePopup: false,
    deleteId: '',
    loading: false,
    showMobileSearch: true,
    searchLoaded: false,
    subBrandAccess: false,
  });

  useEffect(() => {
    const initialFunctions = async () => {
      setState((prev) => ({
        ...prev,
        loading: true,
        cards: [],
      }));

      const res = await dispatch(
        getPlaceholderCardData({
          pageIndex: 1,
          pageSize: state.pageSize,
          archived: !!callArchiveApi,
        }),
      );
      if (res) {
        setState((prev) => ({
          ...prev,
          loading: false,
          archiveSection: !!callArchiveApi,
        }));
      }
    };
    initialFunctions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, callArchiveApi]);

  useEffect(() => {
    const fetchLicensorAccess = async () => {
      const subBrandAccessRes = await dispatch(getSubBrandAccess());
      setState((prev) => ({ ...prev, subBrandAccess: subBrandAccessRes?.data || false }));
    };
    fetchLicensorAccess();
  }, [dispatch]);

  useEffect(() => {
    setState((prev) => ({
      ...prev,
      cards: Array.isArray(placeholderCardList) ? [...placeholderCardList] : [],
    }));
  }, [placeholderCardList]);

  useEffect(() => {
    const count = totalCount;
    const curentList = state.cards;
    if (!state.loading) {
      setState((prev) => ({
        ...prev,
        hasMore: (count ?? 0) > curentList.length,
      }));
    }
  }, [totalCount, state.cards, state.loading]);

  useEffect(() => {
    const delayDebounceFn = setTimeout(async () => {
      if (state.searchLoaded) {
        setState((debouncestate) => ({
          ...debouncestate,
          loading: true,
          pageIndex: 1,
          cards: [],
          selectedIdArray: [],
        }));
        const optns = {
          pageIndex: 1,
          pageSize: state.pageSize,
          searchText: state.searchText,
          archived: state.archiveSection,
        };
        await dispatch(getPlaceholderCardData(optns));
        setState((debstate) => ({
          ...debstate,
          loading: false,
          searchLoaded: false,
        }));
      }
    }, 1000);

    return () => clearTimeout(delayDebounceFn);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.searchText, dispatch, state.archiveSection]);

  const handleNextPageInScroll = async () => {
    let { pageIndex } = state;
    pageIndex += 1;
    setState((nextPagestate) => ({
      ...nextPagestate,
      pageIndex,
      loading: true,
    }));
    const params = {
      pageIndex,
      pageSize: state?.pageSize,
      searchText: state.searchText,
      archived: state.archiveSection,
    };
    await dispatch(getPlaceholderCardData(params));
    setState((nextPagestate) => ({
      ...nextPagestate,
      loading: false,
    }));
  };

  const handlePinCard = async (item: PlaceholderCardItem) => {
    const params = {
      ...item,
      pinned: !item?.pinned,
    };
    const { cards } = state;
    const foundIndex = cards.findIndex((card) => card?._id === item?._id);
    cards[foundIndex]!.pinned = !item?.pinned;
    let newCards: PlaceholderCardItem[] = [];
    const sortedCards = sortBy(cards, ['pinned', 'createdAt']).reverse();
    const pinnedSortedCards = sortedCards.filter((pcpEle: PlaceholderCardItem) => pcpEle.pinned);
    const unpinnedSortedCards = sortedCards.filter((pcpEle: PlaceholderCardItem) => !pcpEle.pinned && !pcpEle.lastOpned);
    const lastOpnedCards = sortedCards.filter((pcpEle: PlaceholderCardItem) => !pcpEle.pinned && pcpEle.lastOpned);
    newCards = [...pinnedSortedCards, ...lastOpnedCards, ...unpinnedSortedCards];
    const res = await dispatch(addPlaceholderCard(params, t));
    if (res) {
      setState((prev) => ({
        ...prev,
        cards: newCards,
      }));
    }
  };

  const handleCardExpand = (item: PlaceholderCardItem) => {
    navigate(`/placeholder-management-card/edit/${item?._id}`, { fromArchive: item.isArchived });
  };

  const ShowunArchivecps = async () => {
    setState((prev) => ({
      ...prev,
      loading: true,
      archiveSection: false,
    }));

    history.push('/placeholder-management');

    await dispatch(
      getPlaceholderCardData({
        pageIndex: 1,
        pageSize: 20,
        archived: false,
      }),
    );

    setState((prev) => ({
      ...prev,
      loading: false,
      searchText: '',
      selectedIdArray: [],
    }));
  };

  const toggleDeleteModal = () => {
    setState((prev) => ({
      ...prev,
      loading: false,
      showDeletePopup: false,
      deleteId: '',
      selectedIdArray: [],
    }));
  };

  const conformDelete = async () => {
    setState((prev) => ({
      ...prev,
      loading: true,
    }));
    let params: Record<string, unknown> = {};
    if (state.deleteId === null) {
      params = {
        pcpIds: [...state.selectedIdArray],
      };
    } else {
      params = { pcpIds: [state.deleteId] };
    }
    const res = await dispatch(deletePlaceholderCardData({ ...params }, t));
    if (res) {
      const oldCards = state.cards;
      let newCards: PlaceholderCardItem[] = [];
      if (state.deleteId) {
        newCards = oldCards.filter((oldEle) => oldEle?._id !== state.deleteId);
      } else {
        newCards = oldCards.filter((oldEle) => !state.selectedIdArray.includes(oldEle?._id));
      }
      setState((oldState) => ({ ...oldState, cards: newCards }));
      toggleDeleteModal();
    }
  };

  const deleteCard = (id: string | null) => {
    setState((prev) => ({
      ...prev,
      showDeletePopup: true,
      deleteId: id,
    }));
  };

  const handleArchiveCard = (id: string | null) => {
    setState((prev) => ({
      ...prev,
      showArchivePopup: true,
      archivedId: id,
    }));
  };
  const toggleArchive = () => {
    setState((prev) => ({
      ...prev,
      loading: false,
      showArchivePopup: false,
      archivedId: '',
      archiveComment: '',
      selectedIdArray: [],
    }));
  };
  const conformArchive = async () => {
    setState((prev) => ({
      ...prev,
      loading: true,
    }));
    let archiveCardDetails: Record<string, unknown> = {};
    if (state.archivedId === null) {
      archiveCardDetails = {
        pcpIds: [...state.selectedIdArray],
        keyName: 'isArchived',
        isArchived: !state.archiveSection,
        archiveComment: state.archiveSection ? '' : state?.archiveComment,
      };
    } else {
      archiveCardDetails = {
        pcpIds: [state.archivedId],
        keyName: 'isArchived',
        isArchived: !state.archiveSection,
        archiveComment: state.archiveSection ? '' : state?.archiveComment,
      };
    }
    const res = await dispatch(archivePlaceholderCardData({ ...archiveCardDetails }, t));
    if (res) {
      const oldCards = state.cards;
      let newCards: PlaceholderCardItem[] = [];
      if (state.archivedId) {
        newCards = oldCards.filter((oldEle) => oldEle?._id !== state.archivedId);
      } else {
        newCards = oldCards.filter((oldEle) => !state.selectedIdArray.includes(oldEle?._id));
      }
      setState((oldState) => ({ ...oldState, cards: newCards }));
      toggleArchive();
    }
  };

  const ShowArchivecps = async () => {
    setState((prev) => ({
      ...prev,
      loading: true,
      archiveSection: true,
    }));
    await dispatch(
      getPlaceholderCardData({
        pageIndex: 1,
        pageSize: 20,
        archived: true,
      }),
    );
    setState((prev) => ({
      ...prev,
      loading: false,
      searchText: '',
      selectedIdArray: [],
    }));
  };

  const archiveCommentChanges = (e: React.ChangeEvent<HTMLInputElement>) => {
    setState((prev) => ({
      ...prev,
      archiveComment: e?.target?.value,
    }));
  };

  const selectedCard = (e: React.ChangeEvent<HTMLInputElement>, id?: string) => {
    if (e?.target?.checked) {
      setState((prev) => ({
        ...prev,
        selectedIdArray: [...state.selectedIdArray, id as string],
      }));
    } else {
      const array = [...state.selectedIdArray];
      const updatedArray = array.filter((arrid) => arrid !== id);
      setState((prev) => ({
        ...prev,
        selectedIdArray: [...updatedArray],
      }));
    }
  };

  const selectableCardIds = uniqBy(state?.cards, '_id')
    .filter((card: PlaceholderCardItem) => !card?.isDefaultCard)
    .map((card: PlaceholderCardItem) => card?._id);
  const isAllSelected = selectableCardIds.length > 0 && selectableCardIds.every((id: string) => state.selectedIdArray.includes(id));

  /** Selects every selectable (non-default) card, or clears the selection. */
  const handleSelectAll = (event: React.ChangeEvent<HTMLInputElement>) => {
    const selectAll = event?.target?.checked;
    setState((prev) => ({
      ...prev,
      selectedIdArray: selectAll ? selectableCardIds : [],
    }));
  };

  const handleSearch = (value: string) => {
    setState((searchState) => ({
      ...searchState,
      searchText: value,
      searchLoaded: true,
    }));
  };

  const deleteMessageInarchive = () => {
    return state.deleteId === null ? t('PLACEHOLDER_MANAGEMENT.NULL_DELETE_ID') : t('PLACEHOLDER_MANAGEMENT.NOT_NULL_DELETE_ID');
  };
  const deleteMessageInUnarchive = () => {
    return state.deleteId === null ? t('PLACEHOLDER_MANAGEMENT.NULL_DELETE_ID') : t('PLACEHOLDER_MANAGEMENT.NOT_NULL_DELETE_ID');
  };

  const handleChatError = useCallback((errorType: string, errorData: unknown) => {
    // eslint-disable-next-line no-console
    console.log('Chat error received:', errorType, errorData);
  }, []);

  const handleResetPlaceholderCards = useCallback(async () => {
    setState((prev) => ({
      ...prev,
      pageIndex: 1,
      cards: [],
      selectedIdArray: [],
    }));
    await dispatch(
      getPlaceholderCardData({
        pageIndex: 1,
        pageSize: state?.pageSize,
        archived: state?.archiveSection,
      }),
    );
    setState((prev) => ({
      ...prev,
      loading: false,
    }));
  }, []);
  return (
    <>
      <div className="page-title-filter sticky pm-nav-header">
        <div className="title-container d-flex align-items-center">
          <h4 className="title">{t('PLACEHOLDER_MANAGEMENT.PLACEHOLDER_TITLE')}</h4>
        </div>

        <div className="search-filter action-icons-container care-library-filter">
          <div style={{ width: 240 }} className="mr-8">
            <NewSearch
              className="bombiix-search"
              value={state?.searchText}
              onChange={handleSearch}
              placeholder={state.archiveSection ? t('PLACEHOLDER_MANAGEMENT.ARCHIVED') : t('PLACEHOLDER_MANAGEMENT.NOT_ARCHIVED')}
              testId="placeholder-management-search"
            />
          </div>
          {!isSupplierLoggedIn && PlaceholderAddEditAccess && (
            <button
              type="button"
              className="bombiix-btn btn-dark"
              onClick={() => {
                if (state.archiveSection) {
                  ShowunArchivecps();
                } else {
                  ShowArchivecps();
                }
              }}
            >
              {state.archiveSection ? 'Open' : 'Archived'}
            </button>
          )}
        </div>
      </div>

      <div className="floting-actions pm-floating-actions">
        {!isSupplierLoggedIn && (
          <>
            {!state.archiveSection && PlaceholderAddEditAccess && (
              <Link className="cursor-pointer text-dark text-decoration-none" to="/placeholder-management-card/add">
                <CustomTooltip placement="top" tooltipText={t('CARE_LABELS.LIBRARY_FILTER.ADD')}>
                  <button type="button" className="btn d-inline-flex p-0" disabled={state.loading} aria-label="Add">
                    <AddIcon size={32} />
                  </button>
                </CustomTooltip>
              </Link>
            )}

            {PlaceholderDeleteAccess && state.archiveSection && (
              <CustomTooltip placement="top" tooltipText={t('COLOR_WAY_CARD.DELETE')}>
                <button
                  type="button"
                  aria-label={t('COMMON_LABEL.DELETE')}
                  onClick={() => deleteCard(null)}
                  className="btn d-inline-flex p-0"
                  disabled={isEmpty(state.selectedIdArray) || state.loading}
                >
                  <DeleteIcon size={32} />
                </button>
              </CustomTooltip>
            )}

            {PlaceholderAddEditAccess && (
              <CustomTooltip placement="top" tooltipText={state.archiveSection ? 'Unarchive' : 'Archive'}>
                <button
                  type="button"
                  aria-label={state.archiveSection ? 'Unarchive' : 'Archive'}
                  onClick={() => handleArchiveCard(null)}
                  className="btn d-inline-flex p-0"
                  disabled={isEmpty(state.selectedIdArray) || state.loading}
                >
                  <ArchiveIcon size={32} />
                </button>
              </CustomTooltip>
            )}
          </>
        )}
      </div>
      {state.loading ? (
        <Loader />
      ) : (
        <>
          {isEmpty(state.cards) && (
            <Container fluid className="py-4">
              <Row>
                <Col md="12">
                  <p className="alert alert-info override-alert-info justify-content-center align-item-center d-flex">
                    {t('PLACEHOLDER_MANAGEMENT.NO_PATH_FOUND_MESSAGE')}
                  </p>
                </Col>
              </Row>
            </Container>
          )}
          {!isEmpty(state?.cards) && !state.loading && !isSupplierLoggedIn && (PlaceholderAddEditAccess || PlaceholderDeleteAccess) && (
            <div className="pm-select-all">
              <div className="common-checkbox size-sm">
                <input type="checkbox" id="pm-select-all" checked={isAllSelected} onChange={handleSelectAll} disabled={selectableCardIds.length === 0} />
                <label htmlFor="pm-select-all" className="chklabel">
                  <span>{t('COMMON_LABEL.SELECT_ALL')}</span>
                </label>
              </div>
            </div>
          )}
          {!isEmpty(state?.cards) && !state.loading && (
            <InfiniteScroll dataLength={state?.cards?.length || 0} loader={state.loading && <Loader />} hasMore={state?.hasMore} next={handleNextPageInScroll}>
              <ResponsiveMasonry columnsCountBreakPoints={{ 350: 1, 750: 2, 900: 3, 1440: 4, 1600: 5 }}>
                <Masonry className="pm-card-grid" gutter="30px">
                  {uniqBy(state?.cards, '_id')?.map((unArchItems: PlaceholderCardItem, unArchIndex: number) => {
                    return (
                      <PlaceholderCardTile
                        key={unArchItems?._id}
                        item={unArchItems}
                        index={unArchIndex}
                        selectedIds={state.selectedIdArray}
                        isSupplierLoggedIn={isSupplierLoggedIn}
                        isArchived={state.archiveSection}
                        onCheck={selectedCard}
                        handlePin={handlePinCard}
                        deleteCard={deleteCard}
                        handleExpand={handleCardExpand}
                        PlaceholderAddEditAccess={PlaceholderAddEditAccess}
                        PlaceholderDeleteAccess={PlaceholderDeleteAccess}
                        subBrandAccess={state.subBrandAccess}
                        archiveCard={handleArchiveCard}
                      />
                    );
                  })}
                </Masonry>
              </ResponsiveMasonry>
            </InfiniteScroll>
          )}
        </>
      )}
      {state.showArchivePopup && (
        <ConfirmationPopup
          onOpen={state.showArchivePopup}
          onConfirm={conformArchive}
          title={`${state.archiveSection ? t('PLACEHOLDER_MANAGEMENT.UNARCHIVE') : t('PLACEHOLDER_MANAGEMENT.ARCHIVE')} ${t(
            'PLACEHOLDER_MANAGEMENT.PLACEHOLDER_TITLE',
          )}`}
          message={`${t('PLACEHOLDER_MANAGEMENT.ARE_U_WANT_TO')} ${
            state.archiveSection ? t('PLACEHOLDER_MANAGEMENT.UNARCHIVES') : t('PLACEHOLDER_MANAGEMENT.ARCHIVES')
          } ${t('PLACEHOLDER_MANAGEMENT.THE_SELECTED_CRITICAL_PATH')}`}
          toggle={toggleArchive}
          isCommentOn={!state.archiveSection}
          commentText={archiveCommentChanges}
          comment={state.archiveComment}
          loading={state.loading}
          showCommentHeader
        />
      )}

      {state.showDeletePopup && (
        <ConfirmationPopup
          onOpen={state.showDeletePopup}
          onConfirm={conformDelete}
          title={t('PLACEHOLDER_MANAGEMENT.DELETE_PRODUCT_CP')}
          message={state.archiveSection ? deleteMessageInarchive() : deleteMessageInUnarchive()}
          toggle={toggleDeleteModal}
          loading={state.loading}
        />
      )}
      {!state.archiveSection && (
        <Chat
          moduleKey="placeholder"
          moduleList={state.cards}
          currentUser={userInfo}
          onChatError={handleChatError}
          onResetItems={handleResetPlaceholderCards}
        />
      )}
    </>
  );
}

export default CardListPage;
